import { merge } from "lodash";
import moment from "moment-timezone";
import {
  customerClassificationEnabled,
  customerHeaderName
} from "@/lib/customerTypes";

import "@mobiscroll/javascript/dist/css/mobiscroll.min.css";
import {
  EventAPIResponse,
  ResourceAPIResponse,
  TimelineEvent,
  TimelineResource,
  UNASSIGNED_RESOURCE_ID
} from "@/typings/scheduler";
import {
  eventcalendar,
  MbscCalendarEvent,
  MbscEventcalendarOptions,
  MbscEventcalendarView,
  momentTimezone
} from "@mobiscroll/javascript";
import { draggable, Eventcalendar } from "@mobiscroll/javascript";

// use moment-timezone for timezone https://mobiscroll.com/docs/javascript/eventcalendar/timezones#the-moment-timezone-library
momentTimezone.moment = moment;

// Which settings bundle the calendar runs with. The mounting component names
// it; the wrapper never reads the window, so it cannot disagree with the layout
// that component was chosen for.
export type CalendarLayout = "desktop" | "mobile";

/**
 * The Sera EventCalendar component.
 *
 * Usage:
 *
 * ```
 * new EventCalendar({
 *   node: HTMLElement;
 *   options: MbscEventcalendarOptions;
 *   layout: "desktop" | "mobile"
 * });
 * ```
 */
export default class EventCalendar extends Eventcalendar {
  node: HTMLElement;
  layout: CalendarLayout;
  // The mobile_web_optimization flag, forwarded from the flag-resolved phone
  // mount through initCalendar. Opt-in only, default false: the desktop mount
  // never passes it and a flag-off phone passes false, so both reach here as
  // the same false this defaults to.
  //
  // It gates the two geometry changes the phone card needs -- the all-day
  // track in setEvents and the arrival-window box in
  // shouldCoverArrivalWindow -- and each pairs it with its own
  // `layout === "mobile"` check, since neither may fire on a desktop board.
  mobileWebOptimization: boolean;
  desktopViewSettings: MbscEventcalendarView = {
    timeline: {
      maxEventStack: 4,
      resolutionHorizontal: "hour",
      type: "day",
      resourceReorder: false,
      zoomLevels: {
        25: {
          // see all hours and calls
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 180,
          timeLabelStep: 60
        },
        50: {
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 60,
          timeLabelStep: 60
        },
        75: {
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 30,
          timeLabelStep: 60
        },
        100: {
          // see 2-4 hours
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 10,
          timeLabelStep: 60
        }
      }
    }
  };
  mobileViewSettings: MbscEventcalendarView = {
    schedule: {
      maxEventStack: 4,
      type: "day"
    }
  };
  mobileOptions: MbscEventcalendarOptions = {
    externalDrop: false,
    dragBetweenResources: false,
    dragInTime: false,
    dragToResize: false,
    dragToCreate: false,
    dragToMove: false
  };
  options: MbscEventcalendarOptions = {
    timezonePlugin: momentTimezone,
    dataTimezone: window.currentTenant.timezone,
    displayTimezone: window.currentTenant.timezone,
    theme: "ios",
    themeVariant: "light",
    groupBy: "resource",
    resources: [],
    data: [],
    renderHeader: this.renderHeader,
    renderDay: () => {},
    zoomLevel: 50,
    externalDrop: true,
    dragBetweenResources: true,
    dragInTime: true,
    dragTimeStep: 30,
    dragToResize: true,
    dragToCreate: true,
    dragToMove: true,
    eventOrder: this.orderEvents,
    colors: [
      {
        start: moment().startOf("year").startOf("day"),
        end: moment().startOf("year").startOf("day").add("hours", 6),
        background: "#f9fafb",
        recurring: {
          repeat: "daily"
        }
      },
      {
        start: moment().startOf("year").endOf("day").subtract("hours", 6),
        end: moment().startOf("year").endOf("day"),
        background: "#f9fafb",
        recurring: {
          repeat: "daily"
        }
      }
    ]
  };
  instance: Eventcalendar | { [key: string]: Eventcalendar };

  constructor(params: {
    node: HTMLElement;
    options?: MbscEventcalendarOptions;
    layout?: CalendarLayout;
    mobileWebOptimization?: boolean;
  }) {
    super();
    this.node = params.node;
    this.layout = params.layout ?? "desktop";
    this.mobileWebOptimization = params.mobileWebOptimization ?? false;
    this.options = merge({}, this.options, params.options);
    let viewSettings = this.desktopViewSettings;
    if (this.layout === "mobile") {
      viewSettings = this.mobileViewSettings;
      this.options = merge({}, this.options, this.mobileOptions);
    }
    this.options.view = merge({}, this.options.view, viewSettings);
    if (!this.node) return;
    this.mount();
  }

  mount() {
    this.instance = eventcalendar(this.node, this.options);
  }

  renderHeader() {
    return "";
  }

  navigate(date) {
    this.instance.navigate(date, true);
  }

  orderEvents(event1: MbscCalendarEvent, event2: MbscCalendarEvent) {
    // Event order:
    // 1. Start Time
    // 2. High priority
    // 3. Alphabetical by title
    if (moment(event1.start).isAfter(event2.start)) return 1;
    if (event1.details.appointment?.priority === "high") return -1;
    return -1;
  }

  setOptions(options: MbscEventcalendarOptions) {
    this.instance.setOptions(options);
  }

  setEvents(data: EventAPIResponse[]) {
    const events = data.reduce((events: TimelineEvent[], event) => {
      if (event.appointment) {
        events.push(
          new TimelineEvent({
            id: event.id,
            title: this.getEventTitle(event),
            start: this.getEventStart(event),
            end: this.getEventEnd(event),
            resource: event.technician?.id || -1,
            ghost: Boolean(event.is_ghost),
            editable:
              !event.is_multi_tech_copy &&
              event.appointment.status === "pending",
            details: event
          })
        );
      } else {
        // G-TB-2253-1c option (a): a calendar event is marked for Mobiscroll's
        // own all-day track only when ALL THREE hold -- the per-mount opt-in
        // (never on for the desktop board or the flag-off phone board), the
        // mobile layout (a mount-time constant, but named explicitly so a
        // future reader does not assume the opt-in alone is the gate), and
        // the event's own event_details.all_day. Missing any one leaves the
        // event exactly as it renders today: an ordinary timed event counted
        // in the lane's overlap stacking.
        const allDay =
          this.mobileWebOptimization &&
          this.layout === "mobile" &&
          event.event_details?.all_day === true;
        event.event_details?.technicians?.forEach((tech) => {
          events.push(
            new TimelineEvent({
              id: event.event_details?.id || -1,
              title: event.event_details?.name || "",
              start: event.event_details?.starts_at || "",
              end: event.event_details?.ends_at || "",
              resource: tech.id,
              ghost: false,
              editable: false,
              details: event,
              allDay
            })
          );
        });
      }
      return events;
    }, []);

    // Through setOptions, not the imperative setEvents: mobiscroll applies the
    // options it was constructed with when its first render commits, and that
    // commit lands after an imperative push made in the same tick -- silently
    // emptying the calendar. `data` is a prop, so the render reads what is set
    // here. This is why resources, which have always gone through setOptions,
    // survive a re-init while events did not.
    return this.instance.setOptions({ data: events });
  }

  setResources(data: ResourceAPIResponse[]) {
    this.instance.setOptions({
      resources: this.buildResources(data)
    });
  }

  destroy(): void {
    this.instance.destroy();
  }

  draggable(
    selector: string,
    options: { dragData: { [k: string]: any } }
  ): void {
    draggable(selector, options);
  }

  buildResources(data: ResourceAPIResponse[]) {
    if (this.layout === "mobile") {
      return data.map(
        (tech) =>
          new TimelineResource({
            id: tech.id,
            color: "",
            name: tech.full_name
          })
      );
    }
    const groupMap: Record<string, ResourceAPIResponse[]> = {};
    const nonGrouped: ResourceAPIResponse[] = [];
    // Group items
    for (const resource of data) {
      if (!resource.department?.name) {
        nonGrouped.push(resource);
        continue;
      }
      if (!groupMap[resource.department.name]) {
        groupMap[resource.department.name] = [];
      }
      groupMap[resource.department.name].push(resource);
    }

    type Group = { name: string; children: TimelineResource[] };
    // Convert to array format with children
    let groups: (Group | TimelineResource)[] = Object.entries(groupMap)
      .sort()
      .map(([group, children], i) => ({
        id: group + i,
        name: group,
        eventCreation: false,
        children: children.map(
          (tech) =>
            new TimelineResource({
              id: tech.id,
              color: "",
              name: tech.full_name
            })
        )
      }));

    groups = nonGrouped
      .map(
        (tech) =>
          new TimelineResource({
            id: tech.id,
            color: "",
            name: tech.full_name,
            cssClass:
              tech.id === UNASSIGNED_RESOURCE_ID
                ? `unassigned-resource-row`
                : ""
          })
      )
      .concat(groups);

    return groups;
  }

  // D-03's own anchor rule. Gated on BOTH the flag and the mobile layout: the
  // phone board mounts whatever the flag says, so a layout check alone widened
  // the box on flag-off tenants too -- a pending 60-minute appointment whose
  // window opens an hour before its scheduled time drew a 2-hour block instead
  // of a 1-hour one, with the overlap stacking that implies, on a board that
  // was supposed to be byte-for-byte unchanged.
  //
  // Every appointment the technician has not yet reached anchors its work
  // segment on `arrival_window_start_time`, and that anchor must sit inside
  // the Mobiscroll box or it is clamped away.
  // The widening is harmless once the appointment has arrived --
  // getEventStart/getEventEnd's own fallback chains already start the box at
  // whichever real timestamp is present, so the box only ever grows by the
  // window's own bounds -- and `completed` is the one status at which the
  // box can close exactly on the appointment's own span, which is why the
  // condition is drawn there rather than at "has a work start".
  private shouldCoverArrivalWindow(event: EventAPIResponse): boolean {
    const appointment = event.appointment;
    if (
      this.mobileWebOptimization &&
      this.layout === "mobile" &&
      appointment?.arrival_window_start_time &&
      appointment?.arrival_window_end_time
    ) {
      return appointment.status !== "completed";
    }
    return false;
  }

  getEventStart(event: EventAPIResponse) {
    if (!event.appointment) return "";
    if (event.is_ghost && event.appointment.ghost_scheduled_time)
      return event.appointment.ghost_scheduled_time;
    const today =
      event.appointment.pre_work_start_time ||
      event.appointment.travel_start_time ||
      event.appointment.work_start_time ||
      event.appointment.arrival_window_start_time;

    if (!this.shouldCoverArrivalWindow(event)) return today;

    // The box only ever grows: the earlier of today's answer and the
    // arrival window start. Both sides are normalised through valueOf()
    // before comparing and the ORIGINAL value that won is returned
    // unchanged, since `today` here is always a string but this method's
    // return contract (to every caller, including
    // useEventCalendar.unassignedEvents) must not change shape.
    const windowStartMs = new Date(
      event.appointment.arrival_window_start_time
    ).valueOf();
    const todayMs = new Date(today).valueOf();
    return windowStartMs < todayMs
      ? event.appointment.arrival_window_start_time
      : today;
  }

  getEventEnd(event: EventAPIResponse) {
    if (!event.appointment) return "";
    // Show default size for 0 minute appointments
    if (event.appointment.estimated_duration_minutes === 0) {
      const start = this.getEventStart(event);
      return moment(start).add(15, "minutes").toISOString();
    }
    if (event.is_ghost && event.appointment.ghost_scheduled_time)
      return moment(event.appointment.ghost_scheduled_time).add(
        event.appointment.estimated_duration_minutes,
        "minutes"
      );

    let today: string | Date;
    switch (event.appointment.status) {
      // When traveling or working, return current time as end time
      case "accepted":
      case "running": {
        today = moment(event.appointment.estimated_end_time).isSameOrAfter(
          new Date()
        )
          ? event.appointment.estimated_end_time
          : new Date();
        break;
      }
      default:
        today =
          event.appointment.work_end_time ||
          event.appointment.estimated_end_time ||
          event.appointment.arrival_window_end_time;
    }

    if (!this.shouldCoverArrivalWindow(event)) return today;

    // The box only ever grows: the later of today's answer and the arrival
    // window end. `today` is a string on every branch above except the
    // accepted/running "still in progress" case, where it is a native
    // Date -- valueOf() normalises both sides before comparing, and the
    // ORIGINAL value that won is returned unchanged (never a normalised
    // one), so this method's existing return contract to every caller is
    // unaffected.
    const windowEndMs = new Date(
      event.appointment.arrival_window_end_time
    ).valueOf();
    const todayMs = new Date(today).valueOf();
    return windowEndMs > todayMs
      ? event.appointment.arrival_window_end_time
      : today;
  }

  getEventTitle(event: EventAPIResponse) {
    if (event.appointment) {
      const { service_category_name, customer } = event.appointment;
      // Flag off keeps the original interpolation, so a nameless customer still
      // reads as it always has.
      const name = customerClassificationEnabled()
        ? customerHeaderName(
            customer?.full_name,
            customer?.company_name,
            customer?.customer_type
          )
        : customer?.full_name;
      return `${name} - ${service_category_name}`;
    }
    return event.event_details?.name || "";
  }
}
